import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { XCircle, ArrowLeft, Star, RefreshCw, HelpCircle } from 'lucide-react';

/**
 * Payment Error Page
 * Shown when payment fails or subscription creation encounters an error
 */
const PaymentError = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const errorMessage = searchParams.get('message') || 'Payment processing failed';

  const handleRetry = () => {
    // Go back to pricing page to try again
    navigate('/pricing');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 border-4 border-red-500">
          {/* Error Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center">
              <XCircle className="w-16 h-16 text-white" />
            </div>
          </div>

          {/* Error Message */}
          <h1 className="text-4xl md:text-5xl font-black text-center mb-4 bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
            Payment Failed
          </h1>

          <p className="text-xl text-gray-700 text-center mb-8 font-semibold">
            We couldn't process your payment. Please try again.
          </p>

          {/* Error Details */}
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-8">
            <h3 className="font-black text-lg text-gray-900 mb-2 flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-red-600" />
              What happened?
            </h3>
            <p className="text-gray-800 font-medium">
              {errorMessage}
            </p>
          </div>

          {/* Common Reasons */}
          <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 mb-8 border-2 border-gray-200">
            <h2 className="text-2xl font-black text-gray-800 mb-4">
              Common Reasons for Payment Failure
            </h2>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700 font-medium">
                  Insufficient funds in your account
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700 font-medium">
                  Incorrect card number, expiration date, or CVV
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700 font-medium">
                  Billing address doesn't match your card
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700 font-medium">
                  Card declined by your bank or card issuer
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700 font-medium">
                  Temporary network or technical issue
                </span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            <button
              onClick={handleRetry}
              className="w-full px-8 py-4 bg-gradient-to-r from-blue-600 to-red-600 text-white text-xl font-black rounded-xl hover:from-blue-700 hover:to-red-700 transition shadow-lg flex items-center justify-center gap-3 border-2 border-blue-800"
            >
              <RefreshCw className="w-6 h-6" />
              Try Again
            </button>

            <button
              onClick={handleGoHome}
              className="w-full px-8 py-4 bg-white text-gray-800 text-lg font-bold rounded-xl hover:bg-gray-50 transition border-2 border-gray-300 flex items-center justify-center gap-3"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </button>
          </div>

          {/* What to Try */}
          <div className="mt-8 bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
            <h3 className="font-black text-lg text-gray-900 mb-3">
              What to Try Next
            </h3>
            <ul className="space-y-2 text-gray-700 font-medium">
              <li>• Double-check your card details and try again</li>
              <li>• Try a different credit or debit card</li>
              <li>• Contact your bank to ensure the card can be used for online purchases</li>
              <li>• Wait a few minutes and try again</li>
            </ul>
          </div>
        </div>

        {/* Support */}
        <div className="text-center mt-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Star className="w-5 h-5 text-blue-600 fill-blue-600" />
            <span className="text-2xl font-black bg-gradient-to-r from-blue-900 via-red-700 to-blue-900 bg-clip-text text-transparent">
              StartPatriot Support
            </span>
          </div>
          <p className="text-gray-600 font-medium">
            Still having trouble? We're here to help!
          </p>
          <p className="text-gray-600 font-medium mt-2">
            Contact us at{' '}
            <a href="mailto:support@startpatriot.com" className="text-blue-600 hover:underline font-bold">
              support@startpatriot.com
            </a>
          </p>
          <p className="text-gray-500 text-sm mt-4">
            Include the error message above when contacting support for faster assistance.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentError;
